import type { FC } from 'react';
import { useSetAtom } from 'jotai';
import {
  VerificationTasksPanel,
  type VerificationVerdict,
} from 'components/verification-tasks-panel';
import type { PreDrillVerificationItem } from 'types/api-responses';
import {
  preDrillVerificationEventAtom,
  preDrillVerificationNoteAtom,
} from './atoms';
import { toVerificationTasks } from './utils';

interface VerificationTasksDrawerProps {
  items: PreDrillVerificationItem[];
  onClose: () => void;
  onVerdictChange: () => void;
}

// Tri-state verdict → the event kind the events endpoint expects.
const EVENT_KIND: Record<VerificationVerdict, string> = {
  correct: 'verification_correct',
  incorrect: 'verification_incorrect',
  reverted: 'verification_reverted',
};

// Pre-drill verification tasks: owns the wire format — verdicts POST
// `{ event: { kind } }`, notes autosave via PATCH; a change triggers re-fetch.
export const VerificationTasksDrawer: FC<VerificationTasksDrawerProps> = ({
  items,
  onClose,
  onVerdictChange,
}) => {
  const postEvent = useSetAtom(preDrillVerificationEventAtom);
  const saveNote = useSetAtom(preDrillVerificationNoteAtom);

  const handleVerdict = (
    taskId: string | number,
    verdict: VerificationVerdict
  ) => {
    postEvent({
      type: 'POST',
      url: `/verification_items/${taskId}/events`,
      data: { event: { kind: EVENT_KIND[verdict] } },
      onSuccess: () => onVerdictChange(),
    });
  };

  const handleNoteSave = (taskId: string | number, note: string) => {
    saveNote({
      type: 'PATCH',
      url: `/verification_items/${taskId}`,
      data: { verification_item: { note } },
      silentLoading: true,
    });
  };

  return (
    <VerificationTasksPanel
      tasks={toVerificationTasks(items)}
      groupLabel="Pre-Drill Verification"
      onClose={onClose}
      onVerdict={handleVerdict}
      onNoteSave={handleNoteSave}
      allowRevert
    />
  );
};
